import { PureComponent, useState, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMobile } from 'react-device-detect';
import { Grid, Icon, Menu, Popup, Tab, Modal, Button } from 'semantic-ui-react';

import { find, compact, findIndex, get, remove } from '../../utils/lodash';
import { CustomSegment, CustomTooltip, FeatureWrapper } from '../../components';
import { isFeature } from '../../utils/activity/userPermission';
import { APP_NAME } from '../../utils/constant';

import {
  OrganizationProfile,
  OrganizationActivity,
  OrganizationGoals,
  OrganizationUserManagement,
  OrganizationGuestManagement,
  OrganizationUserReassign,
  OrganizationVenues,
  OrganizationGLApproval,
  OrganizationReportSettings,
  OrganizationChecklistReport,
  OrganizationAllActivities,
  Communication,
  CrossReference,
} from './forms';

import { exportSystemUsers, getEmptyMessage } from '../../store/actions';
import OrganisationContacts from './forms/contacts';
import { Departments } from './forms';

// TODO:wtf??? the sass for this app is a mess...
import '../organization/organization.scss';
import './organization.scss';

class UnSavedModal extends PureComponent {
  render() {
    const { toggle, leaveThePage, modalShow, t } = this.props;
    return (
      <Modal
        size="tiny"
        open={modalShow}
        closeIcon={
          <Icon onClick={() => toggle()} name="close" className="closeicon" />
        }
      >
        <Modal.Header className="warning-header-text">
          {t('organisation.unsaved_modal_title')}
        </Modal.Header>
        <Modal.Content className="warning-content-text">
          {t('organisation.unsaved_modal_content')}
        </Modal.Content>
        <div className="buttons-stay-leave">
          <Button onClick={() => toggle()} className="stay">
            {t('actions.stay')}
          </Button>
          <Button
            onClick={() => leaveThePage()}
            content={t('actions.leave')}
            className="leave"
          />
        </div>
      </Modal>
    );
  }
}

// Activity sub-tabs component with Users and All Activities
const ActivityTabsContainer = ({ helpTips, user }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const activeSubTab = location.pathname.includes('/search') ? 1 : 0;

  const handleSubTabChange = (e, { activeIndex }) => {
    if (activeIndex === 1) {
      navigate('/organisation/activity/search');
    } else {
      navigate('/organisation/activity/by-user');
    }
  };

  const activitySubPanes = [
    {
      menuItem: t('common.users'),
      render: () => (
        <Tab.Pane attached={false}>
          <Grid>
            <Grid.Row className="org-users-segment">
              <Grid.Column
                mobile={16}
                tablet={16}
                computer={16}
                className="p-0 m-0"
              >
                <CustomSegment
                  title={t('common.users')}
                  children={<OrganizationActivity />}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Tab.Pane>
      ),
    },
    {
      menuItem: t('common.all_activities'),
      render: () => (
        <Tab.Pane attached={false}>
          <OrganizationAllActivities />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Tab
      key={activeSubTab}
      menu={{ borderless: true, attached: false, tabular: true }}
      panes={activitySubPanes}
      activeIndex={activeSubTab}
      onTabChange={handleSubTabChange}
      className="activity-sub-tabs"
    />
  );
};

// Import other required components
const Organization = (props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const { match } = props;

  // Helper to compute initial tab from URL
  const computeInitialTab = () => {
    const locationPath = location.pathname;

    // Map route patterns to their expected tab index
    // This is a fallback before generateTabs() is available
    const routeOrder = [
      'profile',
      'goals',
      'settings',
      'user-management',
      'activity',
      'contacts',
      'venues',
      'communication',
    ];

    // Check if URL contains activity routes
    if (locationPath.includes('/organisation/activity/')) {
      return routeOrder.indexOf('activity');
    }

    // Extract route type from URL if present
    const routeMatch = locationPath.match(/\/organisation\/([^/]+)/);
    if (routeMatch) {
      const routeType = routeMatch[1];
      const idx = routeOrder.indexOf(routeType);
      if (idx !== -1) return idx;
    }

    return 0; // Default to first tab
  };

  const [state, setState] = useState({
    currentTab: computeInitialTab(),
    isEdit: false,
    isLeave: false,
    modalShow: false,
    nextTab: 0,
    crossReference: false,
    department: false,
    crossRefDocOptions: [],
    crossRefCurrentDoc: {},
    reportingYearsOptions: [],
    reportingYear: null,
    reportStart: null,
    reportEnd: null,
  });

  const isEditToggle = (isEdit) => {
    setState((prevState) => ({
      ...prevState,
      isEdit,
    }));
  };

  const {
    user: {
      organisation: { settings = [] },
    },
  } = useSelector((state) => state.authentication);
  const user = useSelector((state) => state.authentication.user);
  const {
    emptyMessage: { goalEmpty = false },
  } = useSelector((state) => state.organization);

  const helpTips = useSelector((state) => state.extra.activityHelpTips);
  console.log('helpTips', helpTips);
  console.log('user', user);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = t('organisation.page_title', { appName: APP_NAME });

    const [config = {}] = settings;

    setState((prevState) => ({
      ...prevState,
      crossReference: config.crossReference ? true : false,
      department: config.department ? true : false,
    }));

    getEmptyMessage();

    return () => {
      setState((prevState) => ({
        ...prevState,
        isEdit: false,
        isLeave: true,
      }));
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const locationPath = location.pathname;

    // Skip navigation if we're already on a valid activity sub-tab route
    if (
      locationPath.includes('/organisation/activity/search') ||
      locationPath.includes('/organisation/activity/by-user')
    ) {
      // Ensure the Activities tab is active
      const tabs = generateTabs();
      const activityTabIndex = findIndex(tabs, (o) => o.route === 'activity');
      if (activityTabIndex !== -1) {
        setState((prevState) => ({
          ...prevState,
          currentTab: activityTabIndex,
        }));
      }
      return;
    }

    if (match.params.type) {
      changeRoute(match.params.type);
    }
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // const toggleCrossRefComponent = () => {
  //   setState(prevState => ({
  //     ...prevState,
  //     crossReference: !state.crossReference,
  //   }));
  // };

  const setCrossRefFromChild = (crossRefDocOptions, crossRefCurrentDoc) => {
    setState((prevState) => ({
      ...prevState,
      crossRefDocOptions,
      crossRefCurrentDoc,
    }));
  };

  // const onChangeCrossReferenceDocument = (event, data) => {
  //   setState(prevState => ({
  //     ...prevState,
  //     crossRefCurrentDocId: data.value,
  //   }));
  // };

  const generateTabs = () => {
    const {
      // crossRefDocOptions, // TODO: This will be needed to next task in cross ref
      crossReference,
      crossRefCurrentDoc,
      crossRefCurrentDocId,
    } = state;
    const panes = compact([
      isFeature(user, 'account_profile')
        ? {
            menuItem: (
              <Menu.Item key="account-profile">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.profile')}
                  position="bottom left"
                >
                  <div>{t('organisation.tabs.profile')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'profile',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <CustomSegment
                  title={t('organisation.update_details')}
                  className="update-organisation-details"
                  children={<OrganizationProfile isEditToggle={isEditToggle} />}
                />
              </Tab.Pane>
            ),
          }
        : null,

      isFeature(user, 'account_goals')
        ? {
            menuItem: goalEmpty ? (
              <Menu.Item key="account-goals">
                <div>
                  <Popup
                    content={<div>{t('organisation.goals_not_created')}</div>}
                    trigger={
                      <Icon
                        name="warning circle"
                        color="red"
                        size="large"
                      ></Icon>
                    }
                  />
                  {t('common.goals')}
                </div>
              </Menu.Item>
            ) : (
              <Menu.Item key="messages">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.goals')}
                  position="bottom left"
                >
                  <div>{t('common.goals')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'goals',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <OrganizationGoals />
              </Tab.Pane>
            ),
          }
        : null,

      /********
      isFeature(user, 'account_core_data')
        ? {
            menuItem: (
              <Menu.Item key="account-core-data">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  contentObject={{
                    data: helpTips,
                    key: '208',
                  }}
                  position="bottom left"
                >
                  <div>Core Data</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'core-data',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <OrganizationCoreData />
              </Tab.Pane>
            ),
          }
        : null,
      ***********/

      isFeature(user, 'account_settings')
        ? {
            menuItem: (
              <Menu.Item key="account-settings">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.settings')} // TODO:help used to be id 209
                  position="bottom left"
                >
                  <div>{t('organisation.tabs.settings')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'settings',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <Grid>
                  <Grid.Row>
                    <Grid.Column
                      className="organization-settings-tab"
                      mobile={16}
                      tablet={16}
                      computer={16}
                    >
                      <CustomSegment
                        title={t('organisation.settings.organization_settings')}
                        children={
                          <OrganizationGLApproval
                            setCrossReference={(value) => {
                              setState((prevState) => ({
                                ...prevState,
                                crossReference: value,
                              }));
                            }}
                            setDepartment={(value) => {
                              setState((prevState) => ({
                                ...prevState,
                                department: value,
                              }));
                            }}
                          />
                        }
                        className="organization-settings"
                      />
                      {crossReference && (
                        <CustomSegment
                          tooltip={{
                            user: props.user,
                            helpTips: props.helpTips,
                            id: '172',
                          }}
                          rightHeader={
                            <div className="ref-document-dropdown">
                              {/* {' '} // TODO: This will be needed to next task in cross ref
                              <Dropdown
                                selection
                                placeholder="Archived Reference Documents"
                                options={crossRefDocOptions}
                                // error={usersError}
                                onChange={(event, data) =>
                                  this.onChangeCrossReferenceDocument(
                                    event,
                                    data
                                  )
                                }
                                icon="angle down"
                              /> */}
                            </div>
                          }
                          extraMargin
                          title={t('organisation.settings.cross_reference')}
                          children={
                            <CrossReference
                              crossRefCurrentDocId={crossRefCurrentDocId}
                              crossReference={crossReference}
                              crossRefCurrentDocFromParent={crossRefCurrentDoc}
                              setCrossRefFromChild={setCrossRefFromChild}
                            />
                          }
                        />
                      )}

                      {state.department && (
                        <CustomSegment
                          tooltip={{
                            user: props.user,
                            helpTips: props.helpTips,
                            id: '172',
                          }}
                          rightHeader={
                            <div className="ref-document-dropdown"></div>
                          }
                          extraMargin
                          title={t('organisation.settings.departments')}
                          children={<Departments user={user} />}
                        />
                      )}

                      <FeatureWrapper
                        featureName="account_checklist"
                        children={
                          <div>
                            <CustomSegment
                              tooltip={{
                                user: props.user,
                                helpTips: props.helpTips,
                                id: '172',
                              }}
                              extraMargin
                              title={t('organisation.settings.checklist.label')}
                              className="checklist"
                              children={<OrganizationChecklistReport />}
                            />
                          </div>
                        }
                      />

                      {get(user, 'organisation.isBetaTester', false) && (
                        <CustomSegment
                          extraMargin
                          title={t('common.reports')}
                          className="report-assistance-settings"
                          children={<OrganizationReportSettings />}
                        />
                      )}
                    </Grid.Column>
                  </Grid.Row>
                </Grid>
              </Tab.Pane>
            ),
          }
        : null,

      isFeature(user, 'account_user_management')
        ? {
            menuItem: (
              <Menu.Item key="account-user-mng">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.user_management')}
                  position="bottom left"
                >
                  <div>{t('organisation.tabs.user_management')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'user-management',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <CustomSegment
                  title={t('organisation.user_management.manage_users')}
                  className="manage-users"
                  children={<OrganizationUserManagement />}
                />

                <FeatureWrapper
                  featureName="account_assign_activities"
                  children={
                    <div>
                      <CustomSegment
                        title={t(
                          'organisation.user_management.reassign_activities'
                        )}
                        className="reassign-activities"
                        extraMargin
                        children={<OrganizationUserReassign />}
                      />
                    </div>
                  }
                />

                <FeatureWrapper
                  featureName="account_view_guest_users"
                  children={
                    <div>
                      <CustomSegment
                        title={t('organisation.user_management.guest_users')}
                        className="guest-users"
                        extraMargin
                        children={<OrganizationGuestManagement />}
                      />
                    </div>
                  }
                />
              </Tab.Pane>
            ),
          }
        : null,

      isFeature(user, 'account_activities')
        ? {
            menuItem: (
              <Menu.Item key="account-activities">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.activities')} // TODO:help used to be id 210
                  position="bottom left"
                >
                  <div>{t('common.activities')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'activity',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <ActivityTabsContainer helpTips={helpTips} user={user} />
              </Tab.Pane>
            ),
          }
        : null,

      isFeature(user, 'account_contractors')
        ? {
            menuItem: (
              <Menu.Item key="account-contractors">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.contacts')} // TODO:help used to be id 211
                  position="bottom left"
                >
                  <div>{t('organisation.tabs.contacts')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'contacts',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <CustomSegment
                  className="manage-contacts"
                  title={t('organisation.contacts.manage_contacts')}
                  children={<OrganisationContacts {...props} />}
                />
              </Tab.Pane>
            ),
          }
        : null,

      isFeature(user, 'account_venues')
        ? {
            menuItem: (
              <Menu.Item key="account-venues">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.venues')}
                  position="bottom left"
                >
                  <div>{t('organisation.tabs.venues')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'venues',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <CustomSegment
                  title={t('organisation.venues.manage_venues')}
                  className="manage-venues"
                  children={<OrganizationVenues />}
                />
              </Tab.Pane>
            ),
          }
        : null,
      isFeature(user, 'account_communication')
        ? {
            menuItem: (
              <Menu.Item key="account-communication">
                <CustomTooltip
                  state={get(user, 'options.tips')}
                  content={t('help.tips.org_admin.tabs.communication')} // TODO:help used to be id 212
                  position="bottom left"
                >
                  <div>{t('common.communication')}</div>
                </CustomTooltip>
              </Menu.Item>
            ),
            route: 'communication',
            render: () => (
              <Tab.Pane attached={false} className="custom-tab">
                <div className="communication-row">
                  <CustomSegment
                    extraMargin
                    title={t('notifications.broadcast_notification')}
                    className="broadcast-notification"
                    children={<Communication isBroadcast={true} />}
                  />
                  <CustomSegment
                    extraMargin
                    title={t('notifications.received_notification')}
                    children={<Communication />}
                  />
                </div>
              </Tab.Pane>
            ),
          }
        : null,
    ]);

    // get user role
    const isAdmin = get(user, 'role', null);
    // remove communication tab is its super admin
    if (isAdmin === 'admin') {
      remove(panes, (i) => i.menuItem === 'Communication');
    }
    return panes;
  };

  const onTabClick = (data) => {
    changeRoute(data.activeIndex);
  };

  const changeRoute = async (value) => {
    try {
      const tabs = generateTabs();
      const [tab, index] = await Promise.all([
        find(tabs, (o, i) => {
          return i === value || o.route === value;
        }),
        findIndex(tabs, (o, i) => {
          return i === value || o.route === value;
        }),
      ]);
      if (tab) {
        setState((prevState) => ({
          ...prevState,
          currentTab: index,
        }));
        navigate(`/organisation/${tab.route}`);
      } else {
        setState((state) => ({
          currentTab: state.prveState,
        }));
      }
    } catch (error) {
      console.error(error);
    }
  };

  const showModalToggle = () => {
    setState((prevState) => ({
      ...prevState,
      modalShow: !prevState.modalShow,
    }));
  };

  const leaveThePage = () => {
    setState((prevState) => ({
      ...prevState,
      modalShow: !prevState.modalShow,
      isEdit: false,
      isLeave: true,
    }));
    onTabClick({ activeIndex: state.nextTab });
  };

  const displayModel = (data) => {
    setState((prevState) => ({
      ...prevState,
      modalShow: true,
      nextTab: data.activeIndex,
    }));
  };

  const { currentTab } = state;

  return (
    <Grid.Row>
      <Tab
        menu={{ borderless: true, attached: false, tabular: false }}
        panes={generateTabs()}
        className={isMobile ? 'custom-tabs-mobile' : 'custom-tabs'}
        activeIndex={currentTab}
        onTabChange={(e, data) => {
          if (!state.isEdit || (!state.isEdit && state.isLeave)) {
            onTabClick(data);
          } else {
            displayModel(data);
          }
        }}
      />
      <UnSavedModal
        modalShow={state.modalShow}
        toggle={() => showModalToggle()}
        leaveThePage={() => leaveThePage()}
        t={t}
      />
    </Grid.Row>
  );
};

export default Organization;
